import { useState, useEffect } from "react";
import type { AxiosInstance } from "axios";
import type { SdwanBranchDetailsResponse } from "../sdwan.types";
import { fetchSdwanBranchDetails } from "../requests/request";
import { readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";

/**
 * Load SD-WAN branch detail (Nivel 3):
 * GET /kontroledge/sdwan/:id/branches/:deviceName?tenantId=
 * Always served live from FortiManager (never persisted).
 */
export function useSdwanBranchDetails(
    sdwanId: string | null,
    branchRef: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    // The last answer for this branch paints at once when the user comes back (a view
    // switch, Back); the request below still runs and replaces it.
    const cacheKey =
        sdwanId && branchRef && tenantId != null
            ? `kontroledge/sdwan/${sdwanId}/branches/${branchRef}?tenantId=${tenantId}`
            : null;
    const [raw, setRaw] = useState<SdwanBranchDetailsResponse | null>(
        () => readCachedFetch<SdwanBranchDetailsResponse>(cacheKey) ?? null
    );
    const [loading, setLoading] = useState(() => !readCachedFetch(cacheKey));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!sdwanId || !branchRef || tenantId == null) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        // Another branch on the same mounted page: its own cached answer or the skeleton —
        // never the previous branch's data.
        const cached = readCachedFetch<SdwanBranchDetailsResponse>(cacheKey);
        setRaw(cached ?? null);
        setLoading(!cached);
        setError(null);

        (async () => {
            try {
                const response = await fetchSdwanBranchDetails(
                    api,
                    tenantId,
                    sdwanId,
                    branchRef
                );
                if (cancelled) return;
                const body = response.data;
                if (!body?.success || !body.data) {
                    throw new Error(body?.message ?? "Failed to load branch");
                }
                writeCachedFetch(cacheKey, body.data);
                setRaw(body.data);
                setError(null);
            } catch (e) {
                console.error("Error in useSdwanBranchDetails:", e);
                if (!cancelled) {
                    const status = (e as { response?: { status?: number } })
                        ?.response?.status;
                    // 404 → branch not found (empty state), even over a cached answer.
                    if (status === 404) {
                        setRaw(null);
                        setError(null);
                        return;
                    }
                    // Other failures (500 / FMG down / network): a cached answer on
                    // screen stays there; with nothing to show → error state with retry.
                    if (!cached) {
                        setRaw(null);
                        setError("We couldn't load this branch. Please try again.");
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sdwanId, branchRef, tenantId, api, refresh, cacheKey]);

    return { raw, loading, error };
}
